import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Globe,
    RefreshCw,
    AlertTriangle,
    CheckCircle2,
    Save,
    ShieldAlert,
    Search,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function formatErr(detail) {
    if (!detail) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

const REGION_LABELS = {
    "us-east-1": "US East (N. Virginia)",
    "us-west-2": "US West (Oregon)",
    "eu-west-1": "EU West (Ireland)",
    "eu-central-1": "EU Central (Frankfurt)",
    "ap-south-1": "Asia Pacific (Mumbai)",
    "ap-southeast-1": "Asia Pacific (Singapore)",
};

export default function RegionsPage() {
    const { user, hasPermission } = useAuth();
    const isSuperAdmin = (user?.role || "").toLowerCase() === "super_admin";
    const canManage = hasPermission("admin.manage");

    const [inventory, setInventory] = useState(null);
    const [violations, setViolations] = useState(null);
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [drafts, setDrafts] = useState({}); // admin_id -> Set of regions
    const [saving, setSaving] = useState({}); // admin_id -> bool

    const load = async () => {
        setLoading(true);
        try {
            const [inv, viol, adminList] = await Promise.all([
                api.get("/admin/regions"),
                api.get("/admin/regions/violations"),
                api.get("/admin/admins"),
            ]);
            setInventory(inv.data);
            setViolations(viol.data);
            const items = Array.isArray(adminList.data) ? adminList.data : (adminList.data?.items || []);
            setAdmins(items);
            // hydrate drafts from current permitted_regions
            const next = {};
            items.forEach((a) => {
                next[a.id] = new Set(a.permitted_regions || []);
            });
            setDrafts(next);
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const regions = useMemo(() => inventory?.regions || [], [inventory]);
    const defaultRegion = inventory?.default_region;

    // Compute totals per region across all collections
    const regionTotals = useMemo(() => {
        const totals = {};
        regions.forEach((r) => { totals[r] = 0; });
        let unspecified = 0;
        Object.values(inventory?.inventory || {}).forEach((perRegion) => {
            Object.entries(perRegion).forEach(([region, count]) => {
                if (region && regions.includes(region)) {
                    totals[region] = (totals[region] || 0) + (count || 0);
                } else {
                    unspecified += (count || 0);
                }
            });
        });
        return { totals, unspecified };
    }, [inventory, regions]);

    const togglePermit = (adminId, region) => {
        setDrafts((p) => {
            const next = { ...p };
            const current = new Set(next[adminId] || []);
            if (current.has(region)) current.delete(region);
            else current.add(region);
            next[adminId] = current;
            return next;
        });
    };

    const isDirty = (admin) => {
        const original = new Set(admin.permitted_regions || []);
        const draft = drafts[admin.id] || new Set();
        if (original.size !== draft.size) return true;
        for (const r of draft) if (!original.has(r)) return true;
        return false;
    };

    const savePermits = async (admin) => {
        const draft = drafts[admin.id] || new Set();
        setSaving((p) => ({ ...p, [admin.id]: true }));
        try {
            const { data } = await api.post(`/admin/regions/permits`, {
                admin_id: admin.id,
                permitted_regions: Array.from(draft),
            });
            const fresh = data?.permitted_regions || [];
            setAdmins((p) => p.map((a) => a.id === admin.id ? { ...a, permitted_regions: fresh } : a));
            setDrafts((p) => ({ ...p, [admin.id]: new Set(fresh) }));
            toast.success(`Permits updated for ${admin.email}`);
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        } finally {
            setSaving((p) => ({ ...p, [admin.id]: false }));
        }
    };

    const filteredAdmins = useMemo(() => {
        if (!search) return admins;
        const q = search.toLowerCase();
        return admins.filter((a) => (a.email || "").toLowerCase().includes(q) || (a.name || "").toLowerCase().includes(q));
    }, [admins, search]);

    return (
        <div className="px-8 py-8" data-testid="regions-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED] flex items-center gap-2"><Globe className="w-3 h-3" /> Multi-Region Deployment</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Regional Data Residency</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Inventory of where each tenant's data physically lives, and per-admin
                        regional permits. Scoped admins are blocked at the API layer from
                        reading data outside their permitted regions.
                    </p>
                </div>
                <button onClick={load} disabled={loading} data-testid="regions-refresh"
                    className="border border-white/15 px-3 py-2 rounded-md text-sm hover:bg-white/5 flex items-center gap-1.5 disabled:opacity-40">
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            {/* Region KPI cards */}
            <div className="grid grid-cols-6 gap-2 mb-5" data-testid="regions-kpis">
                {regions.map((r) => (
                    <div key={r} className="bg-[#0a0a0a] border border-white/10 rounded-md p-3" data-testid={`region-kpi-${r}`}>
                        <div className="flex items-center gap-1.5">
                            <Globe className="w-3 h-3 text-[#7C3AED]" />
                            <span className="text-[10px] uppercase tracking-wider text-white/55 font-mono">{r}</span>
                            {r === defaultRegion && <span className="text-[9px] uppercase tracking-wider text-[#c7a6ff] ml-auto">default</span>}
                        </div>
                        <div className="font-display text-2xl mt-1">{regionTotals.totals?.[r] || 0}</div>
                        <div className="text-[10px] text-white/45 mt-0.5">{REGION_LABELS[r]}</div>
                    </div>
                ))}
            </div>

            {/* Violations banner */}
            {violations && (
                violations.violations.length > 0 ? (
                    <div className="mb-5 p-4 border border-red-300/30 bg-red-300/5 rounded-md flex items-start gap-3" data-testid="regions-violations-banner">
                        <ShieldAlert className="w-4 h-4 mt-0.5 text-red-300" />
                        <div className="flex-1">
                            <div className="text-xs font-mono text-red-200">{violations.total} regional violation(s) detected</div>
                            <div className="text-[11px] text-white/55 mt-1">Entities exist outside your permitted regions ({(violations.permitted_regions || []).join(", ") || "none"}).</div>
                            <div className="text-[11px] font-mono text-white/55 mt-2 space-y-0.5 max-h-32 overflow-auto">
                                {violations.violations.slice(0, 20).map((v, i) => (
                                    <div key={i} data-testid={`region-violation-${i}`}>
                                        {v.collection} · {v.id} · region={v.data_region} · classification={v.classification}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="mb-5 p-3 border border-emerald-300/30 bg-emerald-300/5 rounded-md flex items-center gap-2 text-xs text-emerald-200" data-testid="regions-violations-clean">
                        <CheckCircle2 className="w-4 h-4" /> No regional violations within your permits.
                    </div>
                )
            )}

            {/* Inventory table */}
            <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5 mb-5" data-testid="regions-inventory">
                <div className="flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4 text-[#7C3AED]" />
                    <div className="font-display text-base">Inventory by collection</div>
                    {inventory && (
                        <span className="ml-auto text-[11px] font-mono text-white/45">
                            you are <span className="text-white/75">{inventory.viewer_role}</span> · permits {(inventory.viewer_permitted_regions || []).length === 0 ? "all regions" : (inventory.viewer_permitted_regions || []).join(", ")}
                        </span>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="text-left">
                                <th className="label-xs px-3 py-2">Collection</th>
                                {regions.map((r) => <th key={r} className="label-xs px-3 py-2">{r}</th>)}
                                <th className="label-xs px-3 py-2">Unspecified</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(inventory?.inventory || {}).map(([coll, perRegion]) => {
                                const known = new Set(regions);
                                const unspec = Object.entries(perRegion)
                                    .filter(([region]) => !known.has(region))
                                    .reduce((s, [, c]) => s + (c || 0), 0);
                                return (
                                    <tr key={coll} className="border-t border-white/5" data-testid={`region-row-${coll}`}>
                                        <td className="px-3 py-2 font-mono text-xs">{coll}</td>
                                        {regions.map((r) => (
                                            <td key={r} className="px-3 py-2 font-mono text-xs text-white/70">{perRegion[r] || 0}</td>
                                        ))}
                                        <td className="px-3 py-2 font-mono text-xs text-white/45">{unspec || 0}</td>
                                    </tr>
                                );
                            })}
                            {(!inventory || Object.keys(inventory.inventory || {}).length === 0) && (
                                <tr><td colSpan={regions.length + 2} className="px-3 py-6 text-center text-xs text-white/40 font-mono">No inventory yet</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Admin permits editor */}
            <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5" data-testid="regions-permits">
                <div className="flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4 text-[#7C3AED]" />
                    <div className="font-display text-base">Per-admin regional permits</div>
                    {!canManage && (
                        <span className="ml-auto text-[11px] font-mono text-amber-300 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> read-only (super_admin required to edit)
                        </span>
                    )}
                </div>
                <div className="mb-3 max-w-sm relative">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-white/40" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filter admins by email or name…"
                        data-testid="regions-admin-search"
                        className="w-full bg-[#050505] border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm font-mono"
                    />
                </div>
                <div className="text-[11px] font-mono text-white/45 mb-2">
                    Empty permits = unrestricted (legacy / wildcard). super_admin always sees every region regardless of this list.
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="text-left">
                                <th className="label-xs px-3 py-2">Admin</th>
                                <th className="label-xs px-3 py-2">Role</th>
                                {regions.map((r) => <th key={r} className="label-xs px-3 py-2 text-center">{r.split("-")[0]}<br /><span className="text-white/45">{r.split("-").slice(1).join("-")}</span></th>)}
                                <th className="label-xs px-3 py-2 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={regions.length + 3} className="px-3 py-8 text-center text-xs text-white/40 font-mono">Loading…</td></tr>}
                            {!loading && filteredAdmins.length === 0 && (
                                <tr><td colSpan={regions.length + 3} className="px-3 py-8 text-center text-xs text-white/40 font-mono">No admins match the filter</td></tr>
                            )}
                            {!loading && filteredAdmins.map((admin, idx) => {
                                const isSuper = (admin.role || "").toLowerCase() === "super_admin";
                                const draft = drafts[admin.id] || new Set();
                                const dirty = isDirty(admin);
                                const disabledRow = !canManage || isSuper;
                                return (
                                    <tr key={admin.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`region-admin-row-${idx}`}>
                                        <td className="px-3 py-2">
                                            <div className="text-sm">{admin.email}</div>
                                            <div className="text-[10px] font-mono text-white/45">{admin.name || "—"}</div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${
                                                isSuper ? "border-[#7C3AED]/40 text-[#c7a6ff] bg-[#7C3AED]/10"
                                                : (admin.role || "").toLowerCase() === "viewer" ? "border-white/15 text-white/55"
                                                : "border-emerald-300/30 text-emerald-200 bg-emerald-300/5"
                                            }`}>{admin.role}</span>
                                        </td>
                                        {regions.map((r) => {
                                            const checked = isSuper ? true : draft.has(r);
                                            return (
                                                <td key={r} className="px-3 py-2 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        disabled={disabledRow}
                                                        onChange={() => togglePermit(admin.id, r)}
                                                        data-testid={`region-permit-${idx}-${r}`}
                                                        className="accent-[#7C3AED] disabled:opacity-30"
                                                    />
                                                </td>
                                            );
                                        })}
                                        <td className="px-3 py-2 text-right">
                                            {isSuper ? (
                                                <span className="text-[10px] font-mono text-[#c7a6ff]">all (super)</span>
                                            ) : !canManage ? (
                                                <span className="text-[10px] font-mono text-white/40">—</span>
                                            ) : (
                                                <button
                                                    onClick={() => savePermits(admin)}
                                                    disabled={!dirty || saving[admin.id]}
                                                    data-testid={`region-permit-save-${idx}`}
                                                    className={`px-2.5 py-1 text-[11px] rounded-md flex items-center gap-1 ml-auto transition-colors ${
                                                        dirty
                                                            ? "bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white"
                                                            : "bg-white/5 text-white/40 cursor-default"
                                                    }`}
                                                >
                                                    {saving[admin.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                                    {dirty ? "Save" : "Saved"}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
